/**
 * <gamba-ideas-selector> — los tabs del selector de ideas ("VA CON TODO").
 *
 * TODO EL ESTADO VIVE EN EL DOM. La sección renderiza los cinco tabs, los cinco
 * slides y las cinco filas de pie (nombre de receta + VER RECETA), con el
 * primero activo. Este componente sólo mueve atributos: `aria-pressed` en los
 * tabs y `data-active` en slides y filas. No escribe texto, no arma URLs y no
 * crea nodos, así que sin JS la sección se ve entera con la primera idea
 * seleccionada — que es exactamente el estado inicial del boceto (`ideaTab: 0`).
 *
 * El cross-fade (`opacity .45s ease`) y el rodar de la aceituna
 * (`gamba-ideas-olive-roll`, .42s) los hace el CSS de la sección, incluido su
 * apagado bajo `prefers-reduced-motion: reduce`. Lo único que este archivo
 * necesita del rodar es RE-DISPARARLO, y para eso está `#roll()`.
 *
 * NO SCROLLEA NADA al elegir un tab, igual que el boceto: no hay
 * `scrollIntoView` ni `scroll-behavior`. El riel de tabs de mobile es un
 * `gamba-rail` y su barra de progreso la mide el propio riel leyendo su
 * `scrollLeft`. Por eso nada de acá depende del `installSmoothScroll()` del
 * prototipo, que el §12 del spec prohíbe portar.
 *
 * Refs perezosas, sin cachear: si algún día un `morphSection` reemplaza los
 * hijos de la sección sin desconectar el custom element, una ref cacheada
 * quedaría apuntando a un nodo desprendido. Misma razón que en `gamba-rail.js`.
 * Y como el morph además borra del nodo vivo todo atributo que no venga del
 * servidor, un morph revierte la selección al tab 1 en vez de dejar la sección
 * inconsistente: es el mismo criterio de "degradar a lo que el servidor pintó".
 */

/** Clase que dispara el keyframe del rodar. La define el CSS de la sección. */
const ROLL_CLASS = 'gamba-ideas__olive--roll';

class GambaIdeasSelector extends HTMLElement {
  connectedCallback() {
    // Delegado en el host, que sobrevive a un morph de los hijos.
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * Activa una idea: pinta el tab, cruza el slide y cambia la fila del pie.
   * Volver a elegir la que ya está activa NO es un no-op: re-dispara el rodar
   * de la aceituna, igual que el boceto (que incrementa su `rollTick` en cada
   * click, incluso sobre el tab activo).
   *
   * @param {number} index - Índice de la idea, base 0.
   */
  select(index) {
    if (!Number.isInteger(index) || index < 0) return;

    const tabs = this.querySelectorAll('[data-idea-tab]');
    if (index >= tabs.length) return;

    for (const tab of tabs) {
      const pressed = String(this.#indexOf(tab) === index);
      // Se escribe sólo si cambió: un `setAttribute` con el mismo valor igual
      // invalida el estilo del subárbol del tab.
      if (tab.getAttribute('aria-pressed') !== pressed) tab.setAttribute('aria-pressed', pressed);
    }

    for (const slide of this.querySelectorAll('[data-idea-slide]')) {
      const active = this.#indexOf(slide) === index;

      slide.toggleAttribute('data-active', active);
      /*
       * Los cinco slides quedan montados y superpuestos para que el cross-fade
       * exista, así que los cuatro inactivos hay que sacarlos del árbol de
       * accesibilidad: `opacity: 0` no esconde nada de un lector de pantalla y
       * si no serían cinco `alt` seguidos. No lleva `inert`: adentro del slide
       * no hay nada enfocable.
       */
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    }

    // Las filas del pie se esconden con `display: none`, que ya las saca del
    // árbol de accesibilidad: no necesitan aria-hidden.
    for (const row of this.querySelectorAll('[data-idea-foot]')) {
      row.toggleAttribute('data-active', this.#indexOf(row) === index);
    }

    this.#roll(index);
  }

  /**
   * Re-dispara el rodar de la aceituna del tab activo y lo apaga en las otras.
   *
   * @param {number} index - Índice de la idea que quedó activa.
   */
  #roll(index) {
    for (const olive of this.querySelectorAll('[data-idea-olive]')) {
      olive.classList.remove(ROLL_CLASS);
    }

    const target = this.querySelector(`[data-idea-tab="${index}"] [data-idea-olive]`);
    if (!(target instanceof HTMLElement)) return;

    /*
     * La lectura de layout del medio es OBLIGATORIA y no es cosmética: sin
     * ella el navegador junta el remove y el add en un solo recálculo de
     * estilo, nunca ve al elemento sin la clase y la animación no rearranca.
     * Es el precio de re-disparar un keyframe de CSS desde JS, y es un reflow
     * de un solo elemento chico por click.
     */
    void target.offsetWidth;
    target.classList.add(ROLL_CLASS);
  }

  /**
   * Lee el índice que el Liquid horneó en el atributo del elemento. Los tres
   * atributos (`data-idea-tab`, `data-idea-slide`, `data-idea-foot`) llevan el
   * mismo número para la misma idea.
   *
   * @param {Element} element
   * @returns {number}
   */
  #indexOf(element) {
    const raw =
      element.getAttribute('data-idea-tab') ??
      element.getAttribute('data-idea-slide') ??
      element.getAttribute('data-idea-foot');

    return Number(raw);
  }

  #onClick = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-idea-tab]');
    // Que cada selector maneje sus propios tabs, igual que hace `gamba-rail.js`
    // con los rieles anidados: dos selectores en la misma página son
    // perfectamente posibles (home y una page de recetas).
    if (!tab || tab.closest('gamba-ideas-selector') !== this) return;

    this.select(this.#indexOf(tab));
  };
}

if (!customElements.get('gamba-ideas-selector')) {
  customElements.define('gamba-ideas-selector', GambaIdeasSelector);
}
